/**
 * Which tabs a branch's place shows, and what they are called.
 *
 * WHY THIS IS A PURE FUNCTION AND NOT JSX. The old shell decided each guest
 * action inline, in the middle of a 2,633-line component, with a different
 * gating rule per action — some read the business type, some fetched an API and
 * set state, some read a plan flag, one read a hardcoded boolean. The result was
 * dead-end links (a "Reserve" action on a branch that takes no reservations) and
 * invisible ones (gift cards buried in a collapsed panel). One function, one
 * rule set, testable.
 *
 * THE INVARIANT: a tab is rendered only when there is something behind it, and
 * the set is decided per BRANCH, never per moment. Tabs must not appear and
 * disappear under a guest as their cart fills or a fetch resolves — the rail's
 * shape is part of how they learn the place.
 *
 * WHAT CHANGED. This used to build a fixed four-tab bottom rail. That rail has
 * been retired: a single restaurant's menu is one screen, and app-shell tab
 * furniture on it implied destinations that did not exist while pushing the
 * guest's actual job (their order) into a smaller element on top of it. See
 * components/place/PlaceActionBar.tsx.
 *
 * The capability rules survive intact, because they were never really about
 * tabs — they are the answer to "what can a guest do at this branch at all",
 * and the header, the You sheet and the action bar's standing invitation all
 * still need it. The icons stay so those surfaces can render a destination
 * consistently wherever it appears.
 *
 * @see components/place/PlaceActionBar.tsx
 * @see components/place/PlaceShell.tsx
 */

import { CalendarDays, LayoutGrid, ShoppingBag, User, type LucideIcon } from 'lucide-react';

import { getBusinessType, word } from '@/lib/business-types/registry';

/**
 * One thing a guest can reach at this place. Defined here rather than imported
 * from a chrome component: the rail that owned this type is gone, and the rule
 * set outlived it.
 */
export type PlaceDestination = {
  key: string;
  label: string;
  icon: LucideIcon;
  href?: string;
  absoluteHref?: string;
  onSelect?: () => void;
  badge?: number;
};

export type PlaceCapabilities = {
  businessType?: string | null;
  specificType?: string | null;
  /**
   * The guest can put items in a cart and submit them RIGHT NOW — not merely
   * that the branch has ordering switched on. A branch whose only ordering mode
   * is dine-in offers nothing to a guest who has not scanned a table QR, and an
   * Order tab there is a promise the place cannot keep.
   */
  ordering: boolean;
  /** The branch takes table reservations. */
  reservations: boolean;
  /** The branch takes advance bookings / event enquiries. */
  booking: boolean;
  /** Absolute href for the booking flow, e.g. `/book/<menuId>`. */
  bookingHref?: string | null;
  /** Absolute href for the reservation flow, e.g. `/reserve/<slug>`. */
  reservationHref?: string | null;
};

export type PlaceTabKey = 'menu' | 'order' | 'book' | 'you';

/**
 * Build the rail. `cartCount` only ever changes a badge — never whether a tab
 * exists — so the rail cannot reflow mid-visit.
 */
export function resolvePlaceTabs(
  capabilities: PlaceCapabilities,
  cartCount = 0,
): PlaceDestination[] {
  const { businessType, specificType } = capabilities;
  const tabs: PlaceDestination[] = [];

  // 1. Order — FIRST, because ordering is what a guest on a menu is there to
  //    do; the menu itself stays a tab below it, one tap away. Only where a
  //    cart can actually reach a kitchen or till — a branch with ordering off
  //    gets a browse-only place and no button that lies.
  if (capabilities.ordering) {
    // No href: the cart is a sheet over the menu, not a page. The caller
    // attaches `onSelect`. A destination with neither is not rendered, rather
    // than rendered as a link to nowhere.
    tabs.push({
      key: 'order',
      label: sentenceCase(word(businessType, 'order', specificType)),
      icon: ShoppingBag,
      badge: cartCount,
    });
  }

  // 2. Menu — always. Named in the type's own language, so a caterer's guests
  //    see "Packages" where a diner's see "Menu". Capitalised here because the
  //    vocabulary stores lowercase nouns for use mid-sentence.
  tabs.push({
    key: 'menu',
    label: sentenceCase(word(businessType, 'menu', specificType)),
    icon: LayoutGrid,
    href: '',
  });

  // 3. Book — reservations and advance bookings are one destination for the
  //    guest even though they are two features for us. Splitting them into two
  //    tabs would spend a quarter of the rail on a distinction only the merchant
  //    cares about.
  // These are genuinely separate pages (they were before this redesign and they
  // still are), so they take an ABSOLUTE href rather than one relative to the
  // place root — `/m/<slug>/book` does not exist and never did.
  const bookHref = capabilities.booking ? capabilities.bookingHref : capabilities.reservationHref;
  if ((capabilities.booking || capabilities.reservations) && bookHref) {
    tabs.push({
      key: 'book',
      label: capabilities.booking ? 'Book' : 'Reserve',
      icon: CalendarDays,
      absoluteHref: bookHref,
    });
  }

  // 4. You — orders, receipts, loyalty, gift cards, account. Always present,
  //    because a guest's own history is never empty of meaning: even with no
  //    orders it is where they sign in and find a past receipt.
  // Also a sheet — a guest checking their order history should not lose the
  // menu they were reading.
  tabs.push({ key: 'you', label: 'You', icon: User });

  return tabs;
}

/**
 * Which tab a path inside the place belongs to, for `aria-current` and the
 * active weight. Longest-prefix so `/cart/checkout` still lights up Order.
 */
export function activePlaceTab(
  pathWithinPlace: string,
  tabs: PlaceDestination[],
): string | undefined {
  const path = pathWithinPlace || '';
  let best: PlaceDestination | undefined;

  for (const tab of tabs) {
    // The menu tab's href is '' and would prefix-match everything, so it only
    // wins when nothing else does. Sheet tabs have no href and never match a
    // path — they are opened, not navigated to.
    const href = tab.href;
    if (!href) continue;
    if (path === href || path.startsWith(`${href}/`)) {
      if (!best || href.length > (best.href?.length ?? 0)) best = tab;
    }
  }

  if (best) return best.key;
  return tabs.find((tab) => tab.href === '')?.key;
}

/**
 * The presentation profile's headline conversion action, for the primary button
 * on the place's landing surface. A caterer leads with "Book an event"; a diner
 * leads with the order flow.
 */
export function placePrimaryAction(capabilities: PlaceCapabilities): {
  label: string;
  href: string;
} | null {
  const { businessType, specificType } = capabilities;
  const entry = getBusinessType(businessType).presentation.customerEntry;

  if (entry === 'booking' && capabilities.booking && capabilities.bookingHref) {
    return { label: word(businessType, 'placeOrderCta', specificType), href: capabilities.bookingHref };
  }
  if (capabilities.reservations && capabilities.reservationHref) {
    return { label: 'Reserve a table', href: capabilities.reservationHref };
  }
  return null;
}

function sentenceCase(value: string): string {
  if (!value) return value;
  return value.charAt(0).toUpperCase() + value.slice(1);
}

/**
 * The same vocabulary noun, for use INSIDE a sentence.
 *
 * The registry stores these capitalised ("Order", "Booking", "Package") because
 * most call sites are labels. Dropped into running copy they read as a proper
 * noun — "add it to your Order" — which is the small kind of wrong that makes a
 * product feel machine-written. `sentenceCase` above is its mirror image; both
 * live here so the two directions cannot drift apart.
 */
export function midSentence(value: string): string {
  if (!value) return value;
  // Only the first letter, and only when the word is not an acronym: "QR" must
  // stay "QR", and a merchant vocabulary is free to contain one.
  if (value.length > 1 && value[1] === value[1].toUpperCase() && /[A-Z]/.test(value[1])) return value;
  return value.charAt(0).toLowerCase() + value.slice(1);
}
